import { useState, useEffect, useRef, useCallback } from "react";
import {
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  BookOpen,
  Loader2,
  X,
  Home,
  Library,
  ClipboardList,
  Lightbulb,
  BookMarked,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/chapterReadingPage.css";
import { API_BASE } from "../config";
import gameConfig from "../data/gameConfig.json";
import apiFetch from "../apiFetch";
import { reportDailyTaskCompletion } from "../utils/dailyTaskCompletion";
import { reportHomeworkCompletion } from "../utils/homeworkCompletion";
import { navigateReturn } from "../utils/navigation";

const READING_CFG = gameConfig.bookReading;

function getUserId() {
  const stored = localStorage.getItem("userId") || localStorage.getItem("user_id");
  if (stored) return parseInt(stored, 10);
  try {
    const parsed = JSON.parse(localStorage.getItem("user") || "{}");
    return parsed?.id || parsed?.userId || null;
  } catch {
    return null;
  }
}

// Split text into pages of ~wordsPerPage words, keeping paragraphs intact
function paginateText(text, wordsPerPage = 350) {
  // Normalise any remaining \r, then split on blank lines
  const normalised = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const paragraphs = normalised
    .split(/\n\n+/)
    .map((p) => p.replace(/\n/g, " ").trim())  // join soft-wrapped lines within a paragraph
    .filter(Boolean);
  const pages = [];
  let currentParas = [];
  let wordCount = 0;

  for (const para of paragraphs) {
    const paraWords = para.split(/\s+/).length;
    if (wordCount + paraWords > wordsPerPage && currentParas.length > 0) {
      pages.push(currentParas.join("\n\n"));
      currentParas = [para];
      wordCount = paraWords;
    } else {
      currentParas.push(para);
      wordCount += paraWords;
    }
  }
  if (currentParas.length > 0) pages.push(currentParas.join("\n\n"));
  return pages;
}

// Render page text with per-word spans for hover
function PageContent({ text, onWordHover, onWordLeave }) {
  if (!text) {
    return null;
  }
  // text arriving here is already a joined string of paragraphs separated by \n\n
  // (paginateText joins soft-wrap \n → space, so each element is one clean paragraph)
  const paragraphs = text.split(/\n\n+/).map((p) => p.replace(/\n/g, " ").trim()).filter(Boolean);
  return (
    <div className="crp-page-content">
      {paragraphs.map((para, pi) => (
        <p key={pi} className="crp-para">
          {para.split(/(\s+)/).map((token, ti) => {
            const clean = token.replace(/[^a-zA-Z'-]/g, "").toLowerCase();
            if (clean.length > 2) {
              return (
                <span
                  key={ti}
                  className="crp-word"
                  onMouseEnter={() => onWordHover(clean)}
                  onMouseLeave={onWordLeave}
                >
                  {token}
                </span>
              );
            }
            return <span key={ti}>{token}</span>;
          })}
        </p>
      ))}
    </div>
  );
}

export default function ChapterReadingPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const goBack = () => navigateReturn(navigate, "/bookReading");

  const {
    bookId,
    chapterId,
    bookTitle = "Book",
    chapterTitle = "Chapter",
    chapterDesc = "",
    chapterIndex = 0,
    totalChapters = 1,
    allChapters = [],
    readingOnly = false,
  } = state || {};

  const userId = getUserId();

  // Chapter text + pagination
  const [pages, setPages] = useState([]);
  const [currentSpread, setCurrentSpread] = useState(0);
  const [totalSpreads, setTotalSpreads] = useState(0);
  const [chapterText, setChapterText] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Sidebar modes: 'hint' | 'word' | 'explain'
  const [sidebarMode, setSidebarMode] = useState("hint");
  const [wordDef, setWordDef] = useState(null);
  const [wordLoading, setWordLoading] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [explanation, setExplanation] = useState("");
  const [explainLoading, setExplainLoading] = useState(false);

  // Completion
  const [showCompletion, setShowCompletion] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const [chapterCoinsEarned, setChapterCoinsEarned] = useState(0);

  const hoverTimerRef = useRef(null);

  // Load chapter text — reset all chapter-specific state first so stale
  // values from a previous chapter don't bleed through on same-route navigation.
  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    setCurrentSpread(0);
    setTotalSpreads(0);
    setPages([]);
    setChapterText("");
    setShowCompletion(false);
    setChapterCoinsEarned(0);

    if (!bookId || !chapterId) {
      setLoadError(true);
      setLoading(false);
      return;
    }
    apiFetch(`/api/reading/chapter-text?bookId=${encodeURIComponent(bookId)}&chapterId=${encodeURIComponent(chapterId)}`)
      .then((r) => {
        if (!r.ok) throw new Error("Not found");
        return r.json();
      })
      .then((data) => {
        setChapterText(data.text);
        const ps = paginateText(data.text, 350);
        setPages(ps);
        setTotalSpreads(ps.length);
        setLoading(false);
      })
      .catch(() => {
        setLoadError(true);
        setLoading(false);
      });
  }, [bookId, chapterId]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  const clearSidebar = useCallback(() => {
    setSelectedText("");
    setExplanation("");
    setSidebarMode("hint");
  }, []);

  const handleNext = useCallback(() => {
    if (currentSpread < totalSpreads - 1) {
      setCurrentSpread((s) => s + 1);
      clearSidebar();
    } else if (!showCompletion) {
      markChapterRead();
    }
  }, [currentSpread, totalSpreads, showCompletion, clearSidebar]);

  const handlePrev = useCallback(() => {
    if (currentSpread > 0) {
      setCurrentSpread((s) => s - 1);
      clearSidebar();
    }
  }, [currentSpread, clearSidebar]);

  async function markChapterRead() {
    if (!userId || !bookId || !chapterId) {
      setShowCompletion(true);
      return;
    }
    setMarkingRead(true);
    try {
      const res = await apiFetch(`/api/reading/complete-chapter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, bookId, chapterId }),
      });
      const data = await res.json();
      if (data.firstTime) {
        const coins = READING_CFG.chapterCoins;
        setChapterCoinsEarned(coins);
        await apiFetch(`/api/resources/update-coins`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, delta: coins }),
        });
        await apiFetch(`/api/resources/xp/add`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, delta: coins * READING_CFG.xpMultiplier }),
        });
      }
      reportDailyTaskCompletion();
      reportHomeworkCompletion(null, "game", 1);
    } catch {
      // Non-fatal; still show completion modal
    }
    setMarkingRead(false);
    setShowCompletion(true);
  }

  // Word hover
  const handleWordHover = useCallback((word) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(async () => {
      setSidebarMode("word");
      setWordDef(null);
      setWordLoading(true);
      try {
        const res = await fetch(
          `https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(word)}`,
        );
        if (!res.ok) throw new Error("Not found");
        const data = await res.json();
        const entry = data[0];
        const meaning = entry?.meanings?.[0];
        setWordDef({
          word: entry.word,
          partOfSpeech: meaning?.partOfSpeech || "",
          definition: meaning?.definitions?.[0]?.definition || "No definition found.",
          example: meaning?.definitions?.[0]?.example || "",
        });
      } catch {
        setWordDef({ word, partOfSpeech: "", definition: "Definition not available.", example: "" });
      }
      setWordLoading(false);
    }, 500);
  }, []);

  const handleWordLeave = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  }, []);

  // Text selection
  const handleTextSelection = useCallback(() => {
    const sel = window.getSelection();
    const text = sel?.toString().trim() || "";
    if (!text) return;
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length < 4 || words.length > 50) {
      sel?.removeAllRanges();
      return;
    }
    sel?.removeAllRanges();
    setSelectedText(text);
    setSidebarMode("explain");
    setExplanation("");
    setExplainLoading(true);

    apiFetch(`/api/reading/explain`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ selectedText: text, chapterText, bookTitle }),
    })
      .then((r) => r.json())
      .then((data) => {
        setExplanation(data.explanation || "No explanation available.");
        setExplainLoading(false);
      })
      .catch(() => {
        setExplanation("Could not load explanation. Please try again.");
        setExplainLoading(false);
      });
  }, [chapterText, bookTitle]);

  // Navigate to next chapter
  const handleNextChapter = () => {
    if (chapterIndex + 1 < allChapters.length) {
      setShowCompletion(false);
      const next = allChapters[chapterIndex + 1];
      navigate("/chapterReadingPage", {
        state: {
          bookId,
          chapterId: next.chapter_id,
          bookTitle,
          chapterTitle: `Chapter ${chapterIndex + 2}`,
          chapterDesc: next.chapter_desc,
          chapterIndex: chapterIndex + 1,
          totalChapters,
          allChapters,
          readingOnly,
        },
      });
    }
  };

  if (loading) {
    return (
      <div className="crp-loading">
        <Loader2 size={36} className="crp-spin" />
        <p>Loading chapter…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="crp-error">
        <BookOpen size={40} />
        <p>Chapter text not found.</p>
        <button onClick={() => navigate("/bookReading")}>Back</button>
      </div>
    );
  }

  const pageText = pages[currentSpread] || "";
  const isLastSpread = currentSpread === totalSpreads - 1;
  const progressPct = totalSpreads > 0 ? Math.round(((currentSpread + 1) / totalSpreads) * 100) : 0;

  return (
    <div className="crp-page">

      {/* Header */}
      <header className="crp-header">
        <button className="crp-back-link" onClick={goBack}>
          <ChevronLeft size={16} /> Back
        </button>
        <div className="crp-header-center">
          <span className="crp-chapter-label">{bookTitle} – Chapter {chapterIndex + 1}</span>
          {chapterDesc && <span className="crp-chapter-desc-small">{chapterDesc}</span>}
        </div>
        <div className="crp-header-progress">
          <div className="crp-header-track">
            <div className="crp-header-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <span className="crp-header-pct">{progressPct}%</span>
        </div>
      </header>

      {/* Main layout */}
      <div className="crp-main">

        {/* Book Area */}
        <div className="crp-book-area">

          {/* Page */}
          <div className="crp-spread" onMouseUp={handleTextSelection}>
            <div className="crp-book-page">
              <PageContent
                text={pageText}
                onWordHover={handleWordHover}
                onWordLeave={handleWordLeave}
              />
            </div>
          </div>

          {/* Navigation */}
          <div className="crp-nav">
            <button
              className="crp-nav-btn crp-nav-prev"
              onClick={handlePrev}
              disabled={currentSpread === 0}
            >
              <ArrowLeft size={16} /> Previous
            </button>
            <span className="crp-nav-info">
              Page {currentSpread + 1} of {totalSpreads}
            </span>
            <button
              className="crp-nav-btn crp-nav-next"
              onClick={handleNext}
              disabled={markingRead}
            >
              {isLastSpread ? (markingRead ? <Loader2 size={15} className="crp-spin" /> : "Complete Chapter") : "Next"}
              {!isLastSpread && <ArrowRight size={16} />}
            </button>
          </div>

          <p className="crp-keyboard-hint">← → arrow keys also work</p>
        </div>

        {/* Sidebar */}
        <aside className="crp-sidebar">
          {sidebarMode === "hint" && (
            <div className="crp-sidebar-hint">
              <Lightbulb size={28} className="crp-hint-icon" />
              <p className="crp-hint-title">Reading Tips</p>
              <ul className="crp-hint-list">
                <li><strong>Hover</strong> over any word to see its definition.</li>
                <li><strong>Select</strong> 4–50 words to get an AI explanation of that passage.</li>
                <li>Use ← → keys to turn pages.</li>
              </ul>
            </div>
          )}

          {sidebarMode === "word" && (
            <div className="crp-sidebar-def">
              <div className="crp-sidebar-section-title">
                <BookMarked size={14} /> Word Definition
              </div>
              {wordLoading ? (
                <div className="crp-sidebar-loading">
                  <Loader2 size={20} className="crp-spin" /> Looking up…
                </div>
              ) : wordDef ? (
                <>
                  <div className="crp-def-word">
                    {wordDef.word}
                    {wordDef.partOfSpeech && (
                      <span className="crp-def-pos">{wordDef.partOfSpeech}</span>
                    )}
                  </div>
                  <p className="crp-def-text">{wordDef.definition}</p>
                  {wordDef.example && (
                    <p className="crp-def-example">"{wordDef.example}"</p>
                  )}
                </>
              ) : (
                <p className="crp-sidebar-empty">Move your cursor over a word.</p>
              )}
            </div>
          )}

          {sidebarMode === "explain" && (
            <div className="crp-sidebar-explain">
              <div className="crp-sidebar-section-title">
                <Lightbulb size={14} /> Passage Explanation
                <button className="crp-sidebar-close" onClick={clearSidebar}>
                  <X size={14} />
                </button>
              </div>
              <div className="crp-selected-quote">
                <span className="crp-quote-mark">"</span>
                {selectedText}
                <span className="crp-quote-mark">"</span>
              </div>
              {explainLoading ? (
                <div className="crp-sidebar-loading">
                  <Loader2 size={20} className="crp-spin" /> Generating explanation…
                </div>
              ) : (
                <p className="crp-explain-text">{explanation}</p>
              )}
            </div>
          )}
        </aside>

      </div>

      {/* Completion Modal */}
      {showCompletion && (
        <div className="crp-modal-overlay">
          <div className="crp-modal">
            <div className="crp-modal-icon">
              <BookOpen size={30} color="#fff" />
            </div>
            <h2 className="crp-modal-title">Chapter Complete!</h2>
            <p className="crp-modal-sub">
              You've finished <strong>{chapterTitle}</strong> of <em>{bookTitle}</em>.
            </p>
            {chapterCoinsEarned > 0 && (
              <p className="crp-modal-reward">
                +{chapterCoinsEarned} coins &amp; +{chapterCoinsEarned * READING_CFG.xpMultiplier} XP earned!
              </p>
            )}
            <div className="crp-modal-actions">
              {chapterIndex + 1 < totalChapters && (
                <button className="crp-modal-btn primary" onClick={handleNextChapter}>
                  <ArrowRight size={16} /> Read Next Chapter
                </button>
              )}
              {!readingOnly && (
                <button
                  className="crp-modal-btn secondary"
                  onClick={() => navigate("/bookSelector", { state: { preBookId: bookId, preChapterId: chapterId } })}
                >
                  <ClipboardList size={16} /> Take the Quiz
                </button>
              )}
              <button
                className="crp-modal-btn secondary"
                onClick={() => navigate("/bookReading")}
              >
                <Library size={16} /> Reading Selection
              </button>
              <button
                className="crp-modal-btn ghost"
                onClick={() => navigateReturn(navigate)}
              >
                <ArrowLeft size={16} /> Back
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
